;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();import { useState } from 'react';
import * as yup from 'yup';

import { Button, FormGroup, Input, InputGroup, InputGroupText, ModalBody, ModalFooter } from 'reactstrap';
import { useFormik } from 'formik';

import { MdDelete } from 'react-icons/md';
import { filesize } from 'filesize';
import { logSentryError } from '../../../utils/logSentry';

import encodeBase64 from '../../../utils/encodeBase64';
import { useUser } from '../../../contexts/UserContext';
import readFile from '../../../utils/readFile';
import { getCandidate, saveCandidate } from '../../../api/candidate';
import FormAlert from '../../forms/FormAlert';
import { FormikInputUpload } from '../../forms/inputs';
import { SUPPORTED_FORMATS, initialValues } from '../../forms/ResumeSettings/config';
import getFileIcon from '../../../utils/getFileIcon';
import { Dictionaries } from '../../../i18n/i18n-config';
import { useParams } from 'next/navigation';
import { getI18nUtils } from '../../../i18n/i18n-utils';

const FILE_SIZE = 10000000; // bytes

const validationSchema = yup.object({
  resume: yup
    .mixed()
    .test('fileSize', `Filesize exceeds ${filesize(FILE_SIZE)}`, (value: any) => (value ? value?.size <= FILE_SIZE : true))
    .test('fileType', 'file-extension-not-allowed-for-upload', (value: any) =>
      value ? SUPPORTED_FORMATS.includes(value?.type) : true
    ),
});

type Props = {
  dictionary: Partial<Dictionaries>;
  next: () => void;
  prev: () => void;
};
function ProfileModalResume({ next, prev, dictionary }: Props): JSX.Element {
  const { candidate, setCandidate } = useUser();
  const { formatMessage } = getI18nUtils(dictionary);
  const { locale } = useParams();
  const [alert, setAlert] = useState<{ type: 'success' | 'warning'; message: string } | null>(null);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async ({ resume }, actions) => {
      setAlert(null);
      try {
        if (!resume) {
          setAlert({ type: 'warning', message: 'internal-server-error' });
          actions.setSubmitting(false);
          return;
        }
        const base64 = await readFile(resume);
        await saveCandidate({ resume: { file: encodeBase64(base64 as string), name: resume.name } }, 'acquaintance');
        // "silent" refresh of context
        const c = await getCandidate();
        setCandidate(c);
        actions.setSubmitting(false);
        actions.resetForm();
        next();
      } catch (e: any) {
        setAlert({ type: 'warning', message: 'internal-server-error' });
        actions.setSubmitting(false);
        logSentryError(e);
      }
    },
  });

  const { isSubmitting, handleSubmit, dirty } = formik;

  return (
    <>
      <ModalBody>
        <h2 className="mb-4">{formatMessage({ id: 'profile_add_cv' })}</h2>
        <p>
          {(locale === 'nl' || locale === 'fr') && (
            <p className="mb-4">
              {formatMessage({ id: 'resume-modal-cv-benefit' })}{' '}
              {formatMessage(
                { id: 'mysynergie-settings-uploaded-cv-link' },
                {
                  tipsLink: (
                    <a
                      href={`https://www.synergiejobs.be/${locale}/blog/5f0f11637166f513e68cd9fc/6-tips-voor-een-goed-cv`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#e2001a' }}
                    >
                      {locale === 'fr' ? 'Voici' : 'hier'}
                    </a>
                  ),
                }
              )}
            </p>
          )}
        </p>

        <fieldset disabled={isSubmitting} aria-busy={isSubmitting}>
          {candidate?.cv ? (
            <InputGroup>
              <InputGroupText>{getFileIcon({ color: '#2ec4b6' }, candidate.cvName)}</InputGroupText>
              <Input type="text" name="cv-name" value={candidate.cvName ?? ''} disabled />
              <InputGroupText
                onClick={() => setCandidate((prevC) => ({ ...prevC, cv: undefined }))}
                style={{ cursor: 'pointer' }}
              >
                <MdDelete />
              </InputGroupText>
            </InputGroup>
          ) : (
            <FormGroup className="mb-4">
              <FormikInputUpload
                dictionary={dictionary}
                accept={SUPPORTED_FORMATS.join(',')}
                id="resume"
                name="resume"
                intlKey="apply-form-filetitle"
                formik={formik}
                withoutLabel
                className="mb-1"
              />
              <small>rtf, dockx, doc, pdf - max 5 MB</small>
            </FormGroup>
          )}
        </fieldset>
      </ModalBody>
      <ModalFooter>
        <FormAlert dictionary={dictionary} className="flex-grow-1 mb-2" alert={alert} />
        <Button
          color="primary"
          className="mt-5 w-100"
          onClick={() => {
            if (!dirty) return next();
            handleSubmit();
          }}
        >
          {formatMessage({ id: 'next' })}
        </Button>
        <Button color="white" className="mt-3 mx-auto d-block" onClick={() => prev()}>
          {formatMessage({ id: 'previous' })}
        </Button>
      </ModalFooter>
    </>
  );
}

export default ProfileModalResume;
